import React from "react";

import { formatCurrency, formatPercent } from "../../../../../../utils";
import { Disclosure, DisclosureSummary } from "../styles";
import { withdrawnNoteOf } from "../utils";
import { ValueCompositionProps } from "./types";
import {
  CASHBACK_COLOR,
  INTEREST_COLOR,
  LOSS_COLOR,
  NET_COLOR,
} from "./constants";
import { buildCompositionWidths } from "./utils";
import {
  CompositionCard,
  CompositionHeader,
  CompositionTitle,
  CompositionCaption,
  Bar,
  BarSegment,
  Legend,
  LegendRow,
  LegendLabel,
  Swatch,
  LegendName,
  LegendValue,
  LegendAmount,
  LegendPercent,
  WithdrawnNote,
  InterestBreakdown,
  BreakdownItem,
  BreakdownLabel,
  BreakdownValue,
} from "./styles";

const ValueCompositionComponent: React.FC<ValueCompositionProps> = ({
  netContributed,
  retainedInterest,
  retainedCashback,
  interestEarned,
  taxWithheld,
  grossInterest,
  currentValue,
  interestWithdrawn,
  cashbackWithdrawn,
  isInterestOpenByDefault,
}) => {
  const isLoss = interestEarned < 0;
  const hasCashback = retainedCashback > 0;

  const { segments, netPercent, interestPercent, cashbackPercent } =
    buildCompositionWidths({
      netContributed,
      retainedInterest,
      retainedCashback,
      isLoss,
    });

  const interestLabel = isLoss ? "Interest lost" : "Retained interest";
  const netInterestLabel = isLoss ? "Net loss" : "Net interest";
  // An overdrawn saving has negative retained interest without an earned loss.
  const interestColor = retainedInterest < 0 ? LOSS_COLOR : INTEREST_COLOR;
  const grossInterestText =
    grossInterest === null ? "—" : formatCurrency(grossInterest);
  const hasShares = currentValue > 0;
  const withdrawnNote = withdrawnNoteOf({
    interestWithdrawn,
    cashbackWithdrawn,
  });

  return (
    <CompositionCard role="region" aria-label="Value composition">
      <CompositionHeader>
        <CompositionTitle>Value composition</CompositionTitle>
        <CompositionCaption>Current balance</CompositionCaption>
      </CompositionHeader>

      <Bar>
        {segments.map((segment) => (
          <BarSegment
            data-testid="composition-bar-segment"
            key={segment.color}
            $width={segment.width}
            $color={segment.color}
          />
        ))}
      </Bar>

      <Legend>
        <LegendRow>
          <LegendLabel>
            <Swatch $color={NET_COLOR} />
            <LegendName>Net contributed</LegendName>
          </LegendLabel>
          <LegendValue>
            <LegendAmount data-testid="composition-net-contributed">
              {formatCurrency(netContributed)}
            </LegendAmount>
            {hasShares && (
              <LegendPercent>{formatPercent(netPercent, 0)}</LegendPercent>
            )}
          </LegendValue>
        </LegendRow>

        <LegendRow>
          <LegendLabel>
            <Swatch $color={interestColor} />
            <LegendName>{interestLabel}</LegendName>
          </LegendLabel>
          <LegendValue>
            <LegendAmount
              $color={interestColor}
              data-testid="composition-interest-earned"
            >
              {formatCurrency(retainedInterest)}
            </LegendAmount>
            {hasShares && (
              <LegendPercent>{formatPercent(interestPercent, 0)}</LegendPercent>
            )}
          </LegendValue>
        </LegendRow>

        {hasCashback && (
          <LegendRow>
            <LegendLabel>
              <Swatch $color={CASHBACK_COLOR} />
              <LegendName>Retained cashback</LegendName>
            </LegendLabel>
            <LegendValue>
              <LegendAmount data-testid="composition-retained-cashback">
                {formatCurrency(retainedCashback)}
              </LegendAmount>
              {hasShares && (
                <LegendPercent>
                  {formatPercent(cashbackPercent, 0)}
                </LegendPercent>
              )}
            </LegendValue>
          </LegendRow>
        )}
      </Legend>

      {withdrawnNote && (
        <WithdrawnNote data-testid="composition-withdrawn-note">
          {withdrawnNote}
        </WithdrawnNote>
      )}

      <Disclosure open={isInterestOpenByDefault}>
        <DisclosureSummary>Interest and tax</DisclosureSummary>
        <InterestBreakdown>
          <BreakdownItem>
            <BreakdownLabel>Gross interest</BreakdownLabel>
            <BreakdownValue data-testid="composition-gross-interest">
              {grossInterestText}
            </BreakdownValue>
          </BreakdownItem>
          <BreakdownItem>
            <BreakdownLabel>Tax withheld</BreakdownLabel>
            <BreakdownValue data-testid="composition-tax-withheld">
              {formatCurrency(taxWithheld)}
            </BreakdownValue>
          </BreakdownItem>
          <BreakdownItem>
            <BreakdownLabel>{netInterestLabel}</BreakdownLabel>
            <BreakdownValue data-testid="composition-net-received">
              {formatCurrency(interestEarned)}
            </BreakdownValue>
          </BreakdownItem>
        </InterestBreakdown>
      </Disclosure>
    </CompositionCard>
  );
};

export const ValueComposition = React.memo(ValueCompositionComponent);
